import { Component } from '@angular/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ProductOcrForm } from '../product-ocr-form/product-ocr-form';
import { BaseComponent } from '@app/core/bases';
import { ButtonModule } from 'primeng/button';
import { ProductTempService } from '@app/core/services/product-temp.service';
import { Brand, Category, ProductFilters, ProductTemp, ProductTempBase, Range, SubCategory } from '@app/core/interfaces';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MessageModule } from 'primeng/message';
import { SelectFilterEvent, SelectModule } from 'primeng/select';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { BrandService, CategoryService, ProductService, RangeService, SubCategoryFilters, SubCategoryService } from '@app/core/services';
import { TextareaModule } from 'primeng/textarea';
import { BreadcrumbModule } from 'primeng/breadcrumb';

@Component({
  selector: 'app-product-form',
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    FloatLabelModule,
    MessageModule,
    SelectModule,
    TextareaModule,
    BreadcrumbModule
  ],
  providers: [ConfirmationService, DialogService],
  templateUrl: './product-form.html'
})
export class ProductForm extends BaseComponent {
  isLoadingDatas: boolean = false;
  isSendingDatas: boolean = false;
  productTemp: Partial<ProductTemp> = {
    reference: null,
    description: null,
    marque: null,
    gamme: null,
    categorie: null,
    sous_categorie: null,
  };

  brands: Brand[] = [];
  brandSubject = new Subject<string>();

  ranges: Range[] = [];
  rangeSubject = new Subject<string>();

  references: string[] = [];
  referenceSubject = new Subject<string>();

  categories: Category[] = [];
  categorySubject = new Subject<string>();
  
  subCategories: SubCategory[] = [];
  subCategorySubject = new Subject<string>();

  items: MenuItem[] = [{ label: 'Ajouter un produit' }];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  
  constructor(
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private productTempService: ProductTempService,
    private productService: ProductService,
    private brandService: BrandService,
    private rangeService: RangeService,
    private categoryService: CategoryService,
    private subCategoryService: SubCategoryService,
  ) {
    super();
    this.brandSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.brandRequest(value));
    this.rangeSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.rangeRequest(value));
    this.referenceSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.referenceRequest(value));
    this.categorySubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.categoryRequest(value));
    this.subCategorySubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.subCategoryRequest(value));
  }

  showOcrModal(): void {
    this.dialogRef = this.dialogService.open(ProductOcrForm, {
      width: '600px',
      modal: true,
      focusOnShow: false,
      closable: true,
      styleClass: 'modal-no-content-padding',
      header: `Ajouter des photos`
    });

    this.dialogRef?.onClose.subscribe((data: Partial<ProductTemp> | null) => {
      if (data) {
        if (data.marque?.id)
          this.brandRequest(data.marque.nom || '')

        if (data.gamme?.id)
          this.rangeRequest(data.gamme.nom || '')

        if (data.categorie?.id)
          this.categoryRequest(data.categorie.nom || '')

        if (data.sous_categorie?.id)
          this.subCategoryRequest(data.sous_categorie.nom || '')
        
        setTimeout(() => {
          this.productTemp = { ...data };
        }, 100);
      }
    });
  }

  onBrandFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.brandSubject.next(value);
  }

  brandRequest(value: string) {
    this.brandService
      .getCollection({ nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { marques: 'id,nom' } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.brands = data.results,
      });
  }

  onRangeFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.rangeSubject.next(value);
  }

  rangeRequest(value: string) {
    this.rangeService
      .getCollection({ nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { gammes: 'id,nom' } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.ranges = data.results,
      });
  }

  onReferenceFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.referenceSubject.next(value);
  }

  referenceRequest(value: string) {
    const filter: ProductFilters = { reference: value, size: 10, order_by: 'reference', order: 'ASC', fields: { produits: 'reference' } };
    if (this.productTemp.marque) filter.marque_id = this.productTemp.marque.id;

    this.productService
      .getCollection(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.references = data.results.map(item => item.reference ?? '') || [],
      });
  }

  onCategoryFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.categorySubject.next(value);
  }

  categoryRequest(value: string) {
    this.categoryService
      .getCollection({ nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { categories: 'id,nom' } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.categories = data.results,
      });
  }

  onSubCategoryFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.subCategorySubject.next(value);
  }

  subCategoryRequest(value: string) {
    const filter: SubCategoryFilters = { nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { 'sous_categories': 'id,nom' } };
    if (this.productTemp.categorie) filter.categorie_id = this.productTemp.categorie.id;

    this.subCategoryService
      .getCollection(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.subCategories = data.results,
      });
  }

  resetForm(): void {
    this.productTemp = {
      reference: null,
      description: null,
      marque: null,
      gamme: null,
      categorie: null,
      sous_categorie: null,
    }
  }

  onSubmit(): void {
    if (this.productTemp.id == null) return;
    
    this.isSendingDatas = true;
    const data: Partial<ProductTempBase> = {
      reference: this.productTemp.reference ? this.productTemp.reference.trim() : null,
      description: this.productTemp.description ? this.productTemp.description.trim() : null,
      marque_id: this.productTemp.marque ? this.productTemp.marque.id : null,
      gamme_id: this.productTemp.gamme ? this.productTemp.gamme.id : null,
      categorie_id: this.productTemp.categorie ? this.productTemp.categorie.id : null,
      sous_categorie_id: this.productTemp.sous_categorie ? this.productTemp.sous_categorie.id : null,
    }

    this.productTempService
      .patch(this.productTemp.id, data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.productTemp = data;  
        },
        complete: () => {
          this.onConvertProduct();
        }
      });
  }

  onConvertProduct(): void {
    if (this.productTemp.id) {
      this.productTempService.postConvertir(this.productTemp.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.router.navigate(['/products', data.id_produit], { queryParams: { fusion: data.fusion } });
          },
          complete: () => {
            this.isSendingDatas = false;
            this.resetForm();
          }
        });
    }
  }
}
